import React, { useState, useEffect, useRef } from "react";
import * as RecordRTC from 'recordrtc';
import Header from './header';
import { Icons } from "../assest/icons";
import { ToastContainer, toast } from "react-toastify";
import { logger } from '../utils/logger';
import { useNavigate } from "react-router-dom";
import { getIllnesses, createChatSession, updateChatSession, deleteChatSession, getChatSession, updateUser } from '../services/firestore';
import { saveFHIREncounter, completeFHIREncounter, saveFHIRCondition } from '../services/fhirService';
import { getNextQuestion, isChatComplete, generateChatbotSummary } from './chatbotQuestions';
import { fetchWithAppCheck } from '../utils/fetchWithAppCheck';

function ChatBot() {
  const reportId =  JSON.parse(localStorage.getItem('reportId'))
  const [FinishChat, setFinishChat] = useState(false);
  const [ShowLoader, setShowLoader] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isEmergency, setIsEmergency] = useState(false);
  const [illnessesList, setIllnessesList] = useState([]);
  const [selectedIllness, setSelectedIllness] = useState(null);
  
  // Resolve user ID robustly across legacy/new account shapes.
  const usernameTxt = JSON.parse(localStorage.getItem('UserProfile') || '{}');
  const storedUserId = JSON.parse(localStorage.getItem('UserId') || 'null');
  const resolvedUserId = usernameTxt?.id || usernameTxt?.user_id || storedUserId || null;
  let userId = resolvedUserId;
  if (userId) {
    localStorage.setItem('UserId', JSON.stringify(userId));
  }
  
  const [UserName, setUserName] = useState(usernameTxt?.fullName || 'User');
  const [questions, setQuestions] = useState([]);
  const [answer, setAnswer] = useState('');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(null);
  const [questionLoader, setQuestionLoader] = useState(false);
  const [answerLoader, setAnswerLoader] = useState(false);
  const [nextQuestionLoader, setNextQuestionLoader] = useState(false);
  const messageBoxRef = useRef(null);
  const [openPopup, setOpenPopup] = useState(false);
  const [MessageTxt, setMessageTxt] = useState('');
  const notificationSentRef = useRef(false);
  const pendingNotificationRef = useRef(null);
  const chatCompletedRef = useRef(false);
  // Prevents resumeSession from overriding state after user manually starts a new session
  const newSessionStartedRef = useRef(false);
  
  const socket = useRef(null);
  const recorder = useRef(null);
  const streamRef = useRef(null);
  const loaderTimeoutRef = useRef(null);
  const answerRef = useRef('');
  const [isRecording, setIsRecording] = useState(false);
  const [transcripts, setTranscripts] = useState('');
  const [activeMic, setActiveMic] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false); 
 
  const inputRefAnswer = useRef(null);

  // Ensure the patient's browser timezone is persisted to Firestore so Cloud
  // Functions (e.g. Zoom meeting invitations) can format times correctly.
  useEffect(() => {
    if (!userId) return;
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz) {
      updateUser(userId, { timezone: tz }).catch(() => {});
    }
  }, [userId]);

  useEffect(() => {
    if (inputRefAnswer.current) {
      inputRefAnswer.current.focus();
    }
  }, [answer]);

  // Prompt user to continue previous session or start fresh
  const [resumeAttempted, setResumeAttempted] = useState(false);
  const [showResumePrompt, setShowResumePrompt] = useState(false);
  const pendingResumeRef = useRef(null); // stores { illness, questions, questionIndex, sessionId }

  useEffect(() => {
    const checkForPreviousSession = async () => {
      const savedSessionId = localStorage.getItem('currentChatSessionId');
      const savedIllness = localStorage.getItem('currentChatIllness');
      if (savedSessionId && savedIllness) {
        try {
          const session = await getChatSession(savedSessionId);
          if (session && session.status === 'active') {
            const conversation = session.conversation || [];
            // Only prompt to resume if the user had actually answered at least one question
            if (conversation.length > 0) {
              const answeredMap = new Map(conversation.map(e => [e.question, e.answer]));
              const restoredQuestions = [];
              let nextUnansweredIdx = null;
              for (let i = 0; ; i++) {
                const q = getNextQuestion(savedIllness, i);
                if (!q) break;
                const savedAnswer = answeredMap.get(q.question);
                if (savedAnswer !== undefined) {
                  restoredQuestions.push({ ...q, answer: savedAnswer });
                } else {
                  restoredQuestions.push(q);
                  nextUnansweredIdx = restoredQuestions.length - 1;
                  break;
                }
              }
              // Store the pending resume data and show the prompt — do NOT auto-restore
              pendingResumeRef.current = {
                illness: savedIllness,
                questions: restoredQuestions,
                questionIndex: nextUnansweredIdx !== null ? nextUnansweredIdx : restoredQuestions.length,
                sessionId: savedSessionId,
              };
              setShowResumePrompt(true);
              setResumeAttempted(true);
              return;
            }
          }
        } catch (err) {
          logger.warn('Could not check previous session:', err);
        }
      }
      // No valid previous session — clear any stale localStorage and start fresh
      localStorage.removeItem('currentChatSessionId');
      localStorage.removeItem('currentChatIllness');
      setResumeAttempted(true);
    };
    checkForPreviousSession();
  }, []);

  const handleContinuePreviousSession = () => {
    const data = pendingResumeRef.current;
    if (!data) return;
    setSelectedIllness(data.illness);
    setQuestions(data.questions);
    setCurrentQuestionIndex(data.questionIndex);
    setShowLoader(false);
    setShowResumePrompt(false);
    pendingResumeRef.current = null;
  };

  const handleStartNewSession = async () => {
    const data = pendingResumeRef.current;
    if (data?.sessionId) {
      try {
        await updateChatSession(data.sessionId, { status: 'abandoned' });
      } catch (err) { /* ignore */ }
    }
    localStorage.removeItem('currentChatSessionId');
    localStorage.removeItem('currentChatIllness');
    pendingResumeRef.current = null;
    setShowResumePrompt(false);
  };



const getToken = async () => {
  try {
    const response = await fetchWithAppCheck('https://us-central1-aigicare-prod.cloudfunctions.net/getAssemblyAIToken');
    const data = await response.json();
    return data.token || null;
  } catch (err) {
    logger.error('Failed to get speech token:', err);
    return null;
  }
};

  const generateTranscript = async () => {
    try {
      const token = await getToken();
      if (!token) {
        toast.info('Voice input is temporarily disabled. Please type your answer.', {
          position: toast.POSITION.TOP_RIGHT,
        });
        return;
      }

      setIsConnecting(true);
      socket.current = new WebSocket(`wss://api.assemblyai.com/v2/realtime/ws?sample_rate=16000&token=${token}`);

      const texts = {};
      socket.current.onmessage = (voicePrompt) => {
        let msg = answerRef.current || '';
        const res = JSON.parse(voicePrompt.data);
        if (res.text && res.text.trim() !== '') {
          texts[res.audio_start] = res.text;
        }


        const keys = Object.keys(texts).filter(key => key !== 'undefined').sort((a, b) => a - b);
        keys.forEach((key) => {
          msg += ` ${texts[key]}`;
        });
        setAnswer(msg.trim());
      };

      socket.current.onerror = (event) => {
        socket.current.close();
        socket.current = null;
        setIsConnecting(false);
      };

      socket.current.onclose = (event) => {
        socket.current = null;
        setIsConnecting(false);
      };

      socket.current.onopen = () => {
        setIsConnecting(false);
        setActiveMic('active');
        navigator.mediaDevices.getUserMedia({ audio: true })
          .then((stream) => {
            streamRef.current = stream;
            recorder.current = new RecordRTC(stream, {
              type: 'audio',
              mimeType: 'audio/webm;codecs=pcm',
              recorderType: RecordRTC.StereoAudioRecorder,
              timeSlice: 250,
              desiredSampRate: 16000,
              numberOfAudioChannels: 1,
              bufferSize: 4096,
              audioBitsPerSecond: 128000,
              ondataavailable: (blob) => {
                const reader = new FileReader();
                reader.onload = () => {
                  const base64data = reader.result;
                  if (socket.current) {
                    socket.current.send(JSON.stringify({ audio_data: base64data.split('base64,')[1] }));
                  }
                };
                reader.readAsDataURL(blob);
              },
            });
            recorder.current.startRecording();

          })
          .catch((err) => logger.error('getUserMedia error:', err));
      };
      setIsRecording(true);
    } catch (error) {
      logger.error('Error generating transcript:', error);
      setIsConnecting(false);
    }
  };

  const sendAppointmentNotificationEmails = ({ sessionId, status }) => {
    if (notificationSentRef.current) return;
    notificationSentRef.current = true;
    fetchWithAppCheck('https://us-central1-aigicare-prod.cloudfunctions.net/sendAppointmentNotification', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        patient_name: UserName || 'N/A',
        patient_email: usernameTxt?.email || '',
        illness: selectedIllness || 'N/A',
        session_id: sessionId || 'N/A',
        status: status || 'Pending',
        patient_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      })
    }).catch(err => logger.error('Failed to send appointment notification:', err));
  };

 
  const endTranscription = () => {
    if (socket.current) {
      socket.current.send(JSON.stringify({ terminate_session: true }));
      socket.current.close();
      socket.current = null;
    }

    if (recorder.current) {
      recorder.current.stopRecording();
      recorder.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setActiveMic(null);
    setIsRecording(false);
  };

  const handleMicClick = () => {
    if (isConnecting) {
      return;
    }

    if (activeMic) {
      if (answer.trim() === '') {
        setAnswer(''); // Set to empty string if only spaces
      } else {
        setAnswer(answer);
      } 
      endTranscription();
      setActiveMic(null);
    } else {
      generateTranscript();
      setActiveMic('connecting');
    }
  };

  const resetTranscription = () => {
    setAnswer('');
    setIsRecording(false);

    if (recorder.current) {
      recorder.current.stopRecording();
      recorder.current = null;
    }
    if (socket.current) {
      socket.current.close();
      socket.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    setActiveMic(null);
  };
    
  const handleTextSetAnswer = (e) => {   
    const AnswerTxt =  e.target.value; 
    setAnswer(AnswerTxt);  
    if (AnswerTxt.trim() === '') {
      setAnswer(''); // Set to empty string if only spaces
    } else {
      setAnswer(AnswerTxt);
    } 
    if (inputRefAnswer.current) {
      inputRefAnswer.current.focus();
    }  
  };  
  const handleKeyDown = (e) => { 
    if (!e.shiftKey && e.key === 'Enter') { 
          handleAnswerSubmit();
        }
        
        if (inputRefAnswer.current) {
          inputRefAnswer.current.focus();
        }
  };
  useEffect(() => {
    // Focus input field on initial render
    if (inputRefAnswer.current) {
      inputRefAnswer.current.focus();
    }
  }, []);
 

  // Keep answerRef in sync with answer state to avoid stale closures in WebSocket onmessage
  useEffect(() => {
    answerRef.current = answer;
  }, [answer]);

  useEffect(() => {
    return () => {
      if (recorder.current) {
        recorder.current.stopRecording(() => {
          recorder.current = null;
        });
      }
      if (socket.current) {
        socket.current.close();
        socket.current = null;
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      if (loaderTimeoutRef.current) {
        clearTimeout(loaderTimeoutRef.current);
        loaderTimeoutRef.current = null;
      }
    };
  }, []);

  let navigate = useNavigate();
  const routeChange = () => {
    let path = `/dashboard`;
    navigate(path);
  }

  // Check if userId exists on component mount
  useEffect(() => {
    if (!userId) {
      toast.error('Please log in to access this page.', {
        position: toast.POSITION.TOP_RIGHT,
      });
      navigate('/');
    }
  }, [userId, navigate]);

  // Fetch illnesses
  const illnessesArray = async () => {
    try {
      const illnesses = await getIllnesses();
      setIllnessesList(illnesses);
      loaderTimeoutRef.current = setTimeout(() => setShowLoader(false), 3000);
    } catch (error) {
      logger.error('Error fetching illnesses:', error);
      // Set default list on error
      setIllnessesList([
        'Screening Colon',
        'Acid Reflux',
        'IBS',
        'Abdominal Pain',
        'Rectal Bleeding',
        'Change in Bowel Habits',
        'Diarrhea',
        'Nausea and Vomiting',
        'Difficulty Swallowing',
        'Constipation',
        'Fatty Liver',
        'Upper Endoscopy',
        "Crohn's Disease",
        'Ulcerative Colitis',
        'Anemia',
        'Jaundice',
        'Other Problem'
      ]);
      loaderTimeoutRef.current = setTimeout(() => setShowLoader(false), 3000);
    }
  };
  const handleIllnessSelect = async (item) => {
    // Check if userId exists before making API call
    if (!userId) {
      toast.error('User ID is missing. Please log in again.', {
        position: toast.POSITION.TOP_RIGHT,
      });
      navigate('/');
      return;
    }

    // Close/delete any existing active session before starting a new one
    const existingSessionId = localStorage.getItem('currentChatSessionId');
    if (existingSessionId) {
      try {
        await updateChatSession(existingSessionId, { status: 'abandoned' });
      } catch (err) {
        // Ignore errors from closing old session
      }
      localStorage.removeItem('currentChatSessionId');
      localStorage.removeItem('currentChatIllness');
    }

    // Reset all chat state for a completely fresh session
    setQuestions([]);
    setAnswer('');
    setCurrentQuestionIndex(null);
    setFinishChat(false);
    setMessageTxt('');
    chatCompletedRef.current = false;
    notificationSentRef.current = false;
    pendingNotificationRef.current = null;

    setSelectedIllness(item);
    setAnswerLoader(true);
    setQuestionLoader(true);

    try {
      // Create initial chat session in Firestore
      const sessionData = {
        user_id: userId,
        user_ids: Array.from(new Set([userId, usernameTxt?.id, usernameTxt?.user_id].filter(Boolean))),
        session_id: '', // Will be set after document creation
        session: 'false', // String value for compatibility
        illness: item,
        reports_id: reportId || null,
        conversation: [],
        status: 'active',
        patientstatus: 'New',
        weight: usernameTxt?.weight || '',
        height: usernameTxt?.height || '',
        created_at: new Date().toISOString()
      };

      const sessionId = await createChatSession(sessionData);

      // Update session_id field with the document ID
      await updateChatSession(sessionId, {
        session_id: sessionId
      });

      // FHIR: Save Encounter resource for new session (fire-and-forget)
      saveFHIREncounter({ ...sessionData, status: 'active' }, usernameTxt?.user_id || userId, sessionId);

      // Store session ID and illness for resume on refresh
      localStorage.setItem('currentChatSessionId', sessionId);
      localStorage.setItem('currentChatIllness', item);

      // Get first question from predefined templates (no backend needed!)
      const firstQuestion = getNextQuestion(item, 0);

      if (firstQuestion) {
        setQuestions([firstQuestion]);
        setCurrentQuestionIndex(0);
      }
    } catch (error) {
      logger.error('Error starting chat:', error);
      toast.error(error.message || 'Failed to start chat. Please try again.', {
        position: toast.POSITION.TOP_RIGHT,
      });
    } finally {
      setQuestionLoader(false);
      setAnswerLoader(false);
    }
  };

  useEffect(() => {
    setShowLoader(true);
    try {
      illnessesArray();
    } catch (error) {
      // Error handled silently
    }
  }, []);

  useEffect(() => {
    if (messageBoxRef.current) {
      messageBoxRef.current.scrollIntoView({ behavior: "smooth" });
    }
    if (inputRefAnswer.current) {
      inputRefAnswer.current.focus();
    } 
  }, [questions]);

  const handleAnswerSubmit = async () => {
    if (!answer.trim()) {
      toast.error('Please enter your answer before submitting.', {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }

    if (!userId) {
      toast.error('User ID is missing. Please log in again.', {
        position: toast.POSITION.TOP_RIGHT,
      });
      navigate('/');
      return;
    }

    if (inputRefAnswer.current) {
      inputRefAnswer.current.focus();
    }

    const currentQuestion = questions[currentQuestionIndex];
    const filteredConversation = questions.filter((item) => item.answer && item.answer.trim() !== "");

    setQuestions(prevQuestions =>
      prevQuestions.map((q, i) =>
        i === currentQuestionIndex ? { ...q, answer } : q
      )
    );
    
    const updatedConversation = [
      ...filteredConversation,
      { question: currentQuestion.question, answer: answer }
    ];
    
    setAnswer('');
    setAnswerLoader(true);

    try {
      const sessionId = localStorage.getItem('currentChatSessionId');
      
      // Update session in Firestore with new conversation
      if (sessionId) {
        await updateChatSession(sessionId, {
          conversation: updatedConversation,
          updated_at: new Date().toISOString()
        });
      }
      
      // Check if chat is complete
      const nextIndex = currentQuestionIndex + 1;
      const isEmergencyQuestion = currentQuestion?.question
        ?.toLowerCase()
        .includes('do you need emergency care');
      const isEmergencyYes = isEmergencyQuestion && answer.trim().toLowerCase() === 'yes';

      if (isEmergencyYes) {
        const summary = generateChatbotSummary(selectedIllness, updatedConversation);
        setMessageTxt("Emergency identified. Please call 911 or go to the nearest ER now.");
        setIsEmergency(true);
        chatCompletedRef.current = true;
        setFinishChat(true);
        setAnswerLoader(false);
        setIsPopupOpen(true);
        endTranscription();

        if (sessionId) {
          await updateChatSession(sessionId, {
            status: 'completed',
            session: 'true',
            patientstatus: 'Urgent',
            completed_at: new Date().toISOString(),
            chatbot_summary: summary
          });

          // FHIR: Mark Encounter as finished (only updates status + period.end, preserves start)
          completeFHIREncounter(sessionId, selectedIllness);
          // FHIR: Save initial Condition (illness chosen by patient)
          saveFHIRCondition(selectedIllness, userId, sessionId).catch(err => logger.error('FHIR condition save failed:', err));

          // Trigger AI summary generation in background (fire and forget)
          fetchWithAppCheck('https://us-central1-aigicare-prod.cloudfunctions.net/generateAISummary', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({session_id: sessionId, user_id: userId})
          }).catch(err => logger.error('AI summary generation failed:', err));

          // Send notification immediately when session is saved (do not wait for Close button)
          sendAppointmentNotificationEmails({ sessionId, status: 'Urgent' });
        }
        return;
      }

      const chatComplete = isChatComplete(selectedIllness, nextIndex);
      
      if (chatComplete) {
        // Generate summary
        const summary = generateChatbotSummary(selectedIllness, updatedConversation);
        
        setMessageTxt("Thank you for providing this information. A healthcare provider will review your case and contact you soon.");
        chatCompletedRef.current = true;
        setFinishChat(true);
        setAnswerLoader(false);
        setIsPopupOpen(true);
        endTranscription();
        
        // Update final session status
        if (sessionId) {
          await updateChatSession(sessionId, {
            status: 'completed',
            session: 'true', // Mark as completed session
            patientstatus: 'Pending', // Keep as Pending for admin to review
            completed_at: new Date().toISOString(),
            chatbot_summary: summary
          });

          // FHIR: Mark Encounter as finished (only updates status + period.end, preserves start)
          completeFHIREncounter(sessionId, selectedIllness);
          // FHIR: Save initial Condition (illness chosen by patient)
          saveFHIRCondition(selectedIllness, userId, sessionId).catch(err => logger.error('FHIR condition save failed:', err));

          // Trigger AI summary generation in background (fire and forget)
          fetchWithAppCheck('https://us-central1-aigicare-prod.cloudfunctions.net/generateAISummary', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({session_id: sessionId, user_id: userId})
          }).catch(err => logger.error('AI summary generation failed:', err));

          // Send notification immediately when session is saved (do not wait for Close button)
          sendAppointmentNotificationEmails({ sessionId, status: 'Pending' });
        }
      } else {
        // Get next question from predefined templates
        const nextQuestion = getNextQuestion(selectedIllness, nextIndex);
        
        if (nextQuestion) {
          setAnswer('');
          resetTranscription();
          setQuestions(prevQuestions => [
            ...prevQuestions,
            nextQuestion
          ]);
          setCurrentQuestionIndex(nextIndex);
      
          if (inputRefAnswer.current) {
            inputRefAnswer.current.focus();
          }
        }
      }

    } catch (error) {
      logger.error('Error in chat:', error);
      toast.error(error.message || 'An error occurred. Please try again.', {
        position: toast.POSITION.TOP_RIGHT,
      });
    } finally {
      setAnswer('');
      setAnswerLoader(false);
      resetTranscription();
    }
  };
 
  
  useEffect(() => {
    if (questions.length > 0 && questions[currentQuestionIndex]?.status === "finish") {
      setFinishChat(true);
      setAnswerLoader(false);   
      setIsPopupOpen(true);  
      localStorage.removeItem('reportId');
    } 
      if (inputRefAnswer.current) {
        inputRefAnswer.current.focus();
      } 
  }, [questions, currentQuestionIndex]);

  
  const closePopup = () => {
    if (pendingNotificationRef.current) {
      sendAppointmentNotificationEmails(pendingNotificationRef.current);
      pendingNotificationRef.current = null;
    }
    setIsPopupOpen(false);
    setIsEmergency(false);
    routeChange()
    setQuestions([])
    setCurrentQuestionIndex(null)
    // Clear all appointment persistence data
    localStorage.removeItem('reportId');
    localStorage.removeItem('currentChatSessionId');
    localStorage.removeItem('currentChatIllness');
    localStorage.removeItem('appointmentStep');
    localStorage.removeItem('pageTwoData');
  };
  return (
    <div>
      <div className='container-fluid p-0'>
        <Header />
        <div className='container-fluid p-0 contentBody'>
          <div className="chatbotConversation">
            <h2>Presenting Symptoms</h2>

            <div className="messageListBox">
              <div className="chatbot">
                <span className="imgBox">
                  <img src={Icons[0].chatbot} alt="Chatbot" />
                </span>
                <span>
                  <b className="AI_dateTime">
                    AI 
                  </b>
                  {ShowLoader ? (
                    <div className="showLoaderDot">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  ) : (
                    <>
                      <label className="quesTxt">
                      Hi, How may I help you today? You can select a symptom you're experiencing by clicking on any of the tabs to 
                      start a chat with our AI chatbot. If none of the options match your concern, simply click on the 'Other Problem' tab to begin.
                      {/* Hi, How may I help you today? You may select or type a symptom you're facing currently. */}
                      </label>
                      {showResumePrompt && pendingResumeRef.current ? (
                        <div className="resumePromptBox" style={{ margin: '16px 0', padding: '16px', background: '#f0f4ff', borderRadius: '10px', border: '1px solid #c7d6f7' }}>
                          <p style={{ marginBottom: '12px', fontWeight: '600' }}>
                            You have an unfinished session for <span style={{ color: '#4a6cf7' }}>{pendingResumeRef.current.illness}</span>. Would you like to continue or start a new session?
                          </p>
                          <div style={{ display: 'flex', gap: '10px' }}>
                            <button
                              onClick={handleContinuePreviousSession}
                              style={{ padding: '8px 18px', background: '#4a6cf7', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}
                            >
                              Continue Previous Session
                            </button>
                            <button
                              onClick={handleStartNewSession}
                              style={{ padding: '8px 18px', background: '#fff', color: '#4a6cf7', border: '1px solid #4a6cf7', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}
                            >
                              Start New Session
                            </button>
                          </div>
                        </div>
                      ) : (
                      <div className="questOpt">
                        <b>Please select any</b>
                        <ul className={!selectedIllness ? 'illnessesListOpt' : 'illnessesListOpt selected'}>
                          {illnessesList?.map((item, index) => (
                            <li
                              key={index}
                              className={selectedIllness === item ? 'active' : 'disabled'}
                              onClick={() => handleIllnessSelect(item)}
                              style={{ pointerEvents: selectedIllness && selectedIllness !== item ? 'none' : 'auto' }}
                            >
                              <b>{item}</b>
                            </li>
                          ))}
                        </ul>
                      </div>
                      )}
                    </>
                  )}
                </span>
              </div>

              {questions.length > 0 && (
                <div className="quesListDiv">
                  {questions.map((q, index) => (
                    <div key={index}>
                      <div className="chatbot aiQuest">
                        <span className="imgBox">
                          <img src={Icons[0].chatbot} alt="Chatbot" />
                        </span>
                        <span>
                          {nextQuestionLoader && currentQuestionIndex === index + 1 ? (
                            <div className="showLoaderDot">
                              <span></span>
                              <span></span>
                              <span></span>
                            </div>
                          ) : (
                            <>
                              <b className="AI_dateTime">
                                AI 
                              </b>
                              <label className="quesTxt">{q.question}</label>
                            </>
                          )}
                        </span>
                      </div>
                      {q.answer && (
                        <div className="chatbot userReply">
                          <span className="imgBox">
                            <b>{UserName.split(' ').map(word => word.charAt(0).toUpperCase()).join('')}</b>
                           </span>
                          <span>
                            <b className="AI_dateTime">
                              YOU                             
                            </b>
                            <label className="quesTxt">{q.answer}</label>
                          </span>
                        </div>
                      )}
                      {nextQuestionLoader && currentQuestionIndex === index + 1 && (
                        <div className="chatbot aiQuest">
                          <span className="imgBox">
                            <img src={Icons[0].chatbot} alt="Chatbot" />
                          </span>
                          <span>
                            <div className="showLoaderDot">
                              <span></span>
                              <span></span>
                              <span></span>
                            </div>
                          </span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
               {answerLoader ? ( 
                    <>
                    <div className= "quesListDiv " >
                        <div className={"chatbot aiQuest "  + ( FinishChat ? "d-none" : "")}>
                            <span className="imgBox">
                              <img src={Icons[0].chatbot} alt="Chatbot" />
                            </span>
                            <span> 
                                <div className="showLoaderDot">
                                  <span></span>
                                  <span></span>
                                  <span></span>
                                </div> 
                            </span>
                          </div>
                    </div>
                    </>):('')}
              <div ref={messageBoxRef}></div>
            </div> 
             
       <div className="submitQueryBoxDiv">
              <div className={"QueryInputDivBox " + ( FinishChat ? "d-none" : "")}>
                <div className="inputField">
                  <input
                    type="text"
                    ref={inputRefAnswer} 
                    placeholder={activeMic === 'active' ? 'Mic On ' : "Ask Assistance..."}  
                    value={answer}
                    onChange={handleTextSetAnswer}
                    onKeyDown={handleKeyDown} 
                    disabled={answerLoader || selectedIllness?.length === undefined}
                  />
                  
                  <a onClick={handleMicClick} className={(activeMic === 'active' ? 'deactiveBtnClass ' : 'activeBtnClass')} 
                   disabled={isConnecting || activeMic === 'connecting' || isRecording} >
                      <img src={Icons[0].micIcon} className="logoImg" alt="Mic" />
                    <b className={(activeMic === 'active' ? 'zoom-in-zoom-out' : '')}></b>
                  </a>
                </div>
                <button className="btnSendQuery" onClick={handleAnswerSubmit}>
                  {answerLoader ? (
                    <>
                      Loading
                      <div className="spinner-border text-light" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </>
                  ) : (
                    <>
                      Send
                      <img src={Icons[0].submitArrow} className="logoImg" alt="Submit Arrow" />
                    </>
                  )}
                </button>
              </div>
      </div>
       
          </div>
        </div>
 
       {isPopupOpen ? (<>
        <div className="modal-overlay modelBoxDiv">
          <div className="innerDiv">
            <div className="modal-content">
              {isEmergency ? (<>
                <span className="imgDiv" style={{ background: '#FEE2E2', borderRadius: '50%', width: '60px', height: '60px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                    <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                  </svg>
                </span>
                <h2 style={{ color: '#DC2626', fontWeight: '700', margin: '12px 0 6px' }}>Emergency Detected</h2>
                <p style={{ color: '#555', fontSize: '14px', lineHeight: '1.5', margin: '0 0 14px', textAlign: 'center' }}>Based on your responses, you may need immediate medical attention.</p>
                <div className="emergencyCallBox">
                  <p>Please call 911 immediately</p>
                  <a href="tel:911">Call 911</a>
                </div>
                <ul className="successListPoint">
                  <li>If you are experiencing a life-threatening emergency, call <strong>911</strong> or go to your nearest emergency room right away.</li>
                  <li>Your information has been recorded and flagged as urgent for our medical team.</li>
                  <li>For non-emergency questions, call us at <strong>480-666-3333</strong>.</li>
                </ul>
                <button onClick={closePopup}>Close</button>
              </>) : (<>
                <span className="imgDiv">
                  <img src={Icons[0].CheckIcon} alt="" className="w-100" />
                </span>
                <ul className="successListPoint">
                  <li>Thanks for completing online information.</li>
                  <li>AIGI Care will reach out to you to schedule an online consultation with a provider at a mutually convenient time.</li>
                  <li>You will only be billed after the online consultation is completed.</li>
                  <li>If you have any questions, call us at 480-666-3333.</li>
                </ul>
                <button onClick={closePopup}>Close</button>
              </>)}
            </div>
          </div>
        </div>
       </>): ('') } 

      </div>
    </div>
  );
}

export default ChatBot;
